<template>
  <b-container fluid>
    <h1>Trainer Teams</h1>
    <div v-if="loaded">
      <mio-output
        :method="TrainerTeamSummaryData.method"
        :messages="TrainerTeamSummaryData.messages"
        :tables="TrainerTeamSummaryData.tables"
        size="half"
      />
    </div>
    <h2 v-else>Loading...</h2>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import { set, get, countMembers, validateRow } from '@/utils/MioHelpers';
import MioOutput from '@/components/MioOutput';

export default {
  name: 'MioTrainerTeams',
  components: {
    MioOutput,
  },
  data() {
    return {
      loaded: false,
    }
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_GROUP_COUNT_1: "mio/GET_GROUP_COUNT_1",
      GET_COORDINATOR_COUNT: "mio/GET_COORDINATOR_COUNT"
    }),
    async getData() {
      try {
        await this.GET_GROUP_COUNT_1();
        await this.GET_COORDINATOR_COUNT();

        this.loaded = true;
      } catch (err) {
        console.error(err);
      }
    }
  },
  computed: {
    TrainerTeamSummaryData: {
      get() {
        let input = this.$store.state.mio.groupCount1;
        let inputCoord = this.$store.state.mio.coordinatorCount;
        let working = {};
        let output = {
          method: "Group activity (including teacher counts) per year and country is cross-referenced with the links between teachers and trainers, and the links between trainers and coordinators, to derive the corresponding activity for these entities.",
          messages: [],
        };
        let cols = {};

        // Sort coordinator data seperately
        Object.keys(inputCoord).forEach(row => {
          const today = new Date();
          const currentYear = today.getFullYear();

          if (inputCoord[row].end_year == null) {
            inputCoord[row].end_year = currentYear;
          };

          // Validate
          let [valid, message, yearStarted, yearEnded, country, coordID, teamID] = validateRow(inputCoord[row], ['start_year', 'end_year', 'country', 'coord_uuid', 'team_uuid']);
          if (!valid) {

            output.messages.push(`record is missing some data: ${message}`);
            return;
          }

          // Save country in cols
          cols[country] = {};

          // Data should be counted for all active years
          if (yearEnded > currentYear) { yearEnded = currentYear; }
          for (let year = yearStarted; year <= yearEnded; year++) {

            // Number of regional coordinators
            // See if this row's coordinator already appears for this year and country; if not, save and increment
            let coordinatorsList = get(working, `${year}.${country}.coordinators`);
            if (!coordinatorsList || !Object.keys(coordinatorsList).includes(coordID)) {

              for (let c of [country, 'All']) {
                set(working, `${year}.${c}.coordinators.${coordID}`, {});
                set(working, `coordinators.rows.${year}.${c}`, 1, 'add');
              }
            }
          }
        });

        Object.keys(input).forEach(row => {

          // Validate
          let [valid, message, yearStarted, yearEnded, country, coordID, trainerID, groupID, teacherCount] = validateRow(input[row], ['year_started', 'year_ended', 'country', 'coordinator_uuid', 'trainer_uuid', 'group_uuid', 'teachers_count']);
          if (!valid) {
            output.messages.push(`record is missing some data: ${message}`);
            return;
          }

          // Save country in cols
          cols[country] = {};

          // Data should be counted for all active years
          const today = new Date();
          const currentYear = today.getFullYear();
          if (yearEnded > currentYear) { yearEnded = currentYear; }
          for (let year = yearStarted; year <= yearEnded; year++) {

            // Total trainers
            for (let c of [country, 'All']) {
              set(working, `${year}.${c}.trainers.${trainerID}`, {});
              set(working, `trainers.rows.${year}.${c}`, Object.keys(working[year][c].trainers).length);
            }

            // Average number of trainers per regional coordinator
            // See if this row's trainer has already been saved with this coordinator for this year and country; if not, save and recalculate average
            let trainersList = get(working, `${year}.${country}.coordinators.${coordID}.trainers`);
            if (!trainersList || !trainersList.includes(trainerID)) {
              for (let c of [country, 'All']) {

                // Not recorded for this year and country, add to running list
                set(working, `${year}.${c}.coordinators.${coordID}.trainers`, trainerID, 'push');

                // Recalculate average trainers per coordinator
                let coordinatorsObj = get(working, `${year}.${c}.coordinators`);
                let trainersCount = countMembers(coordinatorsObj, 'trainers');
                set(working, `avgTrainers.rows.${year}.${c}`, (trainersCount / Object.keys(coordinatorsObj).length).toFixed(1));
              }
            }

            // Average number of groups per regional coordinator
            // See if this row's group has already been saved with this coordinator for this year and country; if not, save and recalculate average
            let groupsList = get(working, `${year}.${country}.coordinators.${coordID}.groups`);
            if (!groupsList || !groupsList.includes(groupID)) {
              for (let c of [country, 'All']) {

                // Not recorded for this year and country, add to running list
                set(working, `${year}.${c}.coordinators.${coordID}.groups`, groupID, 'push');

                // Recalculate average groups per coordinator
                let coordinatorsObj = get(working, `${year}.${c}.coordinators`);
                let groupsCount = countMembers(coordinatorsObj, 'groups');
                set(working, `avgGroupsPerCoord.rows.${year}.${c}`, (groupsCount / Object.keys(coordinatorsObj).length).toFixed(1));

                // Recalculate average groups per trainer
                let trainersObj = get(working, `${year}.${c}.trainers`);
                set(working, `avgGroupsPerTrainer.rows.${year}.${c}`, (groupsCount / Object.keys(trainersObj).length).toFixed(1));
              }
            }

            // Average number of teachers per group
            // Add the number of teachers in this group to the running counts and recalculate average
            for (let c of [country, 'All']) {
              set(working, `${year}.${c}.groups.sum`, teacherCount, 'add');
              let sum = get(working, `${year}.${c}.groups.sum`);
              set(working, `${year}.${c}.groups.count`, 1, 'add');
              let count = get(working, `${year}.${c}.groups.count`);
              set(working, `avgTeachers.rows.${year}.${c}`, (sum / count).toFixed(1));
            }
          }
        });


        // Sort and assign cols
        cols = Object.keys(cols).sort().concat('All');
        for (let d of ['coordinators', 'trainers', 'avgTrainers', 'avgGroupsPerCoord', 'avgGroupsPerTrainer', 'avgTeachers']) {
          set(working, `${d}.cols`, cols);
        };

        // Format for output
        output.tables = [
          {
            title: 'Number of Regional Coordinators',
            cols: working.coordinators.cols,
            rows: Object.keys(working.coordinators.rows).sort().reverse().map(year => ({
              title: year,
              data: working.coordinators.cols.map(country => working.coordinators.rows[year][country]),
            })),
          },
          {
            title: 'Number of Trainers',
            cols: working.trainers.cols,
            rows: Object.keys(working.trainers.rows).sort().reverse().map(year => ({
              title: year,
              data: working.trainers.cols.map(country => working.trainers.rows[year][country]),
            })),
          },
          {
            title: 'Average number of Trainers per Regional Coordinator',
            cols: working.avgTrainers.cols,
            rows: Object.keys(working.avgTrainers.rows).sort().reverse().map(year => ({
              title: year,
              data: working.avgTrainers.cols.map(country => working.avgTrainers.rows[year][country]),
            })),
          },
          {
            title: 'Average number of Groups per Regional Coordinator',
            cols: working.avgGroupsPerCoord.cols,
            rows: Object.keys(working.avgGroupsPerCoord.rows).sort().reverse().map(year => ({
              title: year,
              data: working.avgGroupsPerCoord.cols.map(country => working.avgGroupsPerCoord.rows[year][country]),
            })),
          },
          {
            title: 'Average number of Groups per Trainer',
            cols: working.avgGroupsPerTrainer.cols,
            rows: Object.keys(working.avgGroupsPerTrainer.rows).sort().reverse().map(year => ({
              title: year,
              data: working.avgGroupsPerTrainer.cols.map(country => working.avgGroupsPerTrainer.rows[year][country]),
            })),
          },
          {
            title: 'Average number of Teachers per Group',
            cols: working.avgTeachers.cols,
            rows: Object.keys(working.avgTeachers.rows).sort().reverse().map(year => ({
              title: year,
              data: working.avgTeachers.cols.map(country => working.avgTeachers.rows[year][country]),
            })),
          }
        ];

        // console.log(working);
        // console.log(output);
        return output;
      }
    },
  },
};
</script>
